<template>
  <div>
    <template v-if="hasBenefits">
      <UlTabs
        v-model="tab"
        :items="currentProviderBenefitsData.genders.map(gen => gen.name[$i18n.locale])"
      />
      <v-tabs-items v-model="tab" class="mt-6">
        <v-tab-item
          v-for="(gender, indexGender) in currentProviderBenefitsData.genders"
          :key="indexGender"
        >
          <div
            v-for="(benefit, indexBenefit) in filteredBenefitsForGender(gender)"
            :key="gender.benefits.indexOf(benefit)"
            class="d-flex justify-start bottom-border"
            :class="{
              'pt-4': indexBenefit === 0,
              'pt-6': indexBenefit > 0,
              'pb-4': indexBenefit === filteredBenefitsForGender(gender).length - 1,
              'pb-6': indexBenefit < filteredBenefitsForGender(gender).length - 1
            }"
          >
            <div>
              <v-img
                width="40"
                height="40"
                contain
                :src="$asset(`img/icons/${benefit.icon}.svg`)"
                class="mr-4"
                data-cy="healthInsuranceProviderBenefitsDetailImage"
              />
            </div>
            <div>
              <div class="mb-2">
                <strong>{{ benefit.type[$i18n.locale] }}</strong>
              </div>
              <div
                class="mb-2 grey-darken-1--text"
                data-cy="healthInsuranceProviderBenefitsDetailText"
              >
                {{ benefit.benefit[$i18n.locale] }}
              </div>
              <v-expand-transition hide-on-leave>
                <div
                  v-if="
                    showedDescription ===
                    indexGender.toString() + gender.benefits.indexOf(benefit).toString()
                  "
                  class="grey-darken-1--text"
                  v-html="detailText(benefit)"
                />
              </v-expand-transition>
              <div
                class="mt-2 d-flex primary--text link"
                @click="toggleDescription(gender, benefit)"
              >
                <div>
                  {{
                    $t(
                      `healthInsuranceProviderBenefits.${
                        showedDescription ===
                        indexGender.toString() + gender.benefits.indexOf(benefit).toString()
                          ? 'hideDetail'
                          : 'showDetail'
                      }`
                    )
                  }}
                </div>
                <v-icon
                  class="rotate primary--text"
                  :class="{
                    'rotate-180':
                      showedDescription ===
                      indexGender.toString() + gender.benefits.indexOf(benefit).toString()
                  }"
                >
                  $dropSmall
                </v-icon>
              </div>
            </div>
          </div>
          <i18n
            path="healthInsuranceProviderBenefits.fullInfo.base"
            tag="div"
            class="mt-4 text-center grey-darken-1--text"
          >
            <template #link>
              <div class="d-inline-block">
                <v-icon class="d-inline-block" color="primary">$newTab</v-icon>
                <span
                  class="link--inline"
                  @click="newTab(currentProviderBenefitsData.companyBenefitsLink)"
                  >{{ $t('healthInsuranceProviderBenefits.fullInfo.linkText') }}</span
                >
              </div>
            </template>
          </i18n>
        </v-tab-item>
      </v-tabs-items>
    </template>
    <div v-else class="text-center" data-cy="healthInsuranceProviderBenefitsNoBenefitsMessage">
      <v-img
        width="56"
        height="56"
        class="mb-6 mx-auto"
        :src="$asset('img/icons/person-sad.svg')"
      ></v-img>
      <div class="text-h1 mb-2">{{ $t('healthInsuranceProviderBenefits.empty.title') }}</div>
      <div
        class="provider-text mx-auto grey-darken-1--text"
        v-html="$t('healthInsuranceProviderBenefits.empty.text')"
      ></div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import currentHealthInsuranceProvider from '../mixins/currentHealthInsuranceProvider';
import partnerInfo from '~/mixins/partnerInfo';

export default {
  name: 'HealthInsuranceProviderBenefitsDetail',
  mixins: [currentHealthInsuranceProvider, partnerInfo],
  data() {
    return {
      showedDescription: null,
      selectedCategory: null
    };
  },
  watch: {
    tab() {
      this.selectedCategory = null;
      this.showedDescription = null;
    },
    selectedCategory() {
      this.showedDescription = null;
    }
  },
  computed: {
    ...mapGetters({
      insuranceCompanyList: 'forms/insuranceCompany/insuranceCompanyList'
    }),
    tab: {
      get() {
        return this.$store.state.profile.healthInsuranceProviderBenefit.tab;
      },
      set(value) {
        this.$store.commit(`profile/healthInsuranceProviderBenefit/setTab`, value);
      }
    }
  },
  created() {
    if (this.$auth.user.gender === 2) {
      this.tab = 1;
    } else {
      this.tab = 0;
    }
  },
  methods: {
    categoriesForGender(gender) {
      return [...new Set(gender.benefits.map(benefit => benefit.category).filter(Boolean))];
    },
    filteredBenefitsForGender(gender) {
      if (!this.selectedCategory) {
        return gender.benefits;
      }
      return gender.benefits.filter(benefit => benefit.category === this.selectedCategory);
    },
    detailText(benefit) {
      //this is not perfect, but necessary because of input data structure (imported from .csv)
      //import file requirements are listed in section "Technický popis" in story https://ulekare.atlassian.net/browse/DEVUL-4117
      let linkText = 'odkaz';
      if (this.$i18n.locale === 'en') {
        linkText = 'link';
      }
      return (
        benefit.description[this.$i18n.locale].slice(
          0,
          benefit.description[this.$i18n.locale].lastIndexOf(linkText)
        ) + `<a href="${benefit.url}" target="_blank" class="link--inline">${linkText}</a>.`
      ).replace(/^\s+|\s+$/gm, '$&<br>');
    },
    toggleDescription(gender, benefit) {
      const indexGender = this.currentProviderBenefitsData.genders.indexOf(gender);
      const indexBenefit = gender.benefits.indexOf(benefit);
      if (this.showedDescription === indexGender.toString() + indexBenefit.toString()) {
        this.showedDescription = null;
      } else {
        this.showedDescription = indexGender.toString() + indexBenefit.toString();
        this.sendAnalyticsUl({
          activity: 'visit_detail_health_insurance',
          partner_name: this.partnerName,
          partner_id: this.partnerId
        });
      }

      this.gtmPush({
        event: 'feature',
        'event.detail': `detail_${this.showedDescription === null ? 'hide' : 'show'}`,
        'feature.detail': `${gender.name[this.$i18n.locale]} - ${benefit.type[this.$i18n.locale]}`
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.bottom-border {
  border-bottom: $border-width solid $grey-lighten;
}

.rotate {
  transition: transform 0.3s;
}

.provider-text {
  max-width: 300px;
}
</style>
